import type { AImageProps } from '../../atoms/AImage/AImage.types';
import type { MButtonIconProps } from '../MButtonIcon/MButtonIcon.types';
import type { AScrimGradientProps } from '../../atoms/AScrimGradient/AScrimGradient.types';
import type { MTagProps } from '../MTag/MTag.types';
import type { MCarouselControlsType } from '../MCarouselControls/MCarouselControls.types';
import type { ResponsiveImageSpec } from '../../utils/ImageUtils';
import type { MMediaImageItem, MMediaImageLogoContainer } from '../MMediaImage/MMediaImage.types';

export enum MMediaCarouselCarouselStyle {
  Standard = 'Standard',
  Compact = 'Compact',
}

export interface MMediaCarouselMediaImageItem {
  aImage?: AImageProps;
  mButtonIcon?: MButtonIconProps;
  aScrimGradient?: AScrimGradientProps;
  mTag?: MTagProps;
  mLogoContainer?: MMediaImageLogoContainer;
}

/**
 * Legacy AEM-driven slide item shape.
 * Kept for backward compatibility with existing renderer integrations.
 */
export interface MMediaImageMultiItem extends MMediaImageItem {
  logo?: string;
  aLogo32?: { brand?: string };
  iconButton?: string;
}

export interface MMediaCarouselCarouselArrow {
  /** AEM may deliver this as the string `'true'`; accept both forms. */
  disabled?: boolean | string;
  transparent?: boolean | string;
}

export interface MMediaCarouselCarouselControls {
  type?: MCarouselControlsType;
}

export interface MMediaCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  carouselStyle?: MMediaCarouselCarouselStyle;
  /**
   * Carousel pagination style rendered by `MCarouselControls`.
   */
  carouselType?: MCarouselControlsType;
  /**
   * @deprecated Use `carouselType` instead. This is temporarily preserved for
   * AEM page models until the dialog migration is complete.
   */
  mCarouselControls?: MMediaCarouselCarouselControls;
  /**
   * When true, navigation arrows are hidden on initial render and revealed on
   * pointer hover or keyboard focus-within. Applies to the `Standard`
   * carousel style only; ignored for `Compact`, where the
   * `MCarouselControls` arrows are used and are always visible.
   */
  hover?: boolean;
  /**
   * Master switch for the per-slide `MLogoContainer` overlay.
   * - `true`: each slide renders its own `mLogoContainer` (if present).
   * - `false` (default): hides every slide's logo, regardless of per-slide config.
   *
   * Useful for variants where the same slide data is reused but the brand
   * mark should be suppressed (e.g., a section that already has a header
   * logo above the carousel).
   */
  logo?: boolean;
  /**
   * New/normalized slide list.
   */
  mMediaImage?: MMediaCarouselMediaImageItem[];
  /**
   * Legacy AEM slide list.
   */
  mMediaImageMulti?: MMediaImageMultiItem[];
  /**
   * Optional responsive rendition specs for images (mobile/tablet/desktop aspect ratios).
   */
  renditionSpecs?: ResponsiveImageSpec[];
  aCarouselArrow?: MMediaCarouselCarouselArrow;
  /**
   * Additional CSS class names. Use this to pass theme tokens (`standard`,
   * `inverse`, `alternate`) from the parent organism. Whitespace-separated
   * tokens are de-duplicated, and if no theme token is present the carousel
   * falls back to `inverse`. Theme tokens are sourced from the shared
   * `Themes` enum.
   */
  className?: string;
  /**
   * Accessible name for the carousel region. Applied as `aria-label` on the
   * root wrapper, which also exposes `role="region"` and
   * `aria-roledescription="carousel"` so assistive tech announces it as a
   * carousel landmark on entry (e.g., NVDA: "Image carousel, region").
   * Defaults to `'Image carousel'` if not provided.
   */
  ariaLabel?: string;
  /**
   * Optional per-slide rendition specs resolver. When provided, overrides the
   * carousel-level `renditionSpecs` for each slide.
   */
  slideRenditionSpecsResolver?: (
    item: MMediaCarouselMediaImageItem | MMediaImageMultiItem,
    index: number
  ) => ResponsiveImageSpec[] | undefined;
  /**
   * Optional per-slide CSS class resolver applied to each slide content wrapper.
   */
  slideClassNameResolver?: (
    item: MMediaCarouselMediaImageItem | MMediaImageMultiItem,
    index: number
  ) => string | undefined;
}
